// Keyboard shortcuts (listed in the help dialog in index.html; keep in step).
//   G / Esc      hide / restore the controls (Esc also cancels a region pick)
//   F            fullscreen        0  fit        + / -  zoom        T  underlying tiling
//   [ ] ← →      previous / next iteration
//   Space        play / pause the drawing        R  replay
//   S            save the view as PNG            D  save a region
//   1 … 6        choose a curve (grid order)
import type { App } from '../app.ts';
import type { Capture } from '../ui/capture.ts';
import { toggleFullscreen } from '../ui/focus.ts';

export function mountKeyboard(app: App, cap: Capture): void {
  document.addEventListener('keydown', (ev) => {
    if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
    if (document.querySelector<HTMLDialogElement>('dialog[open]')) return;
    const k = ev.key.toLowerCase();
    const tag = (ev.target as HTMLElement | null)?.tagName || '';
    const inField = tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA';
    if (inField && !['g', 'escape', '[', ']'].includes(k)) return;
    if (inField && (ev.target as HTMLInputElement).type === 'text') return;

    const handled = () => ev.preventDefault();
    if (k === 'escape') {
      if (cap.picking) { cap.setPicking(false); return handled(); }
      if (app.state.uiHidden) { app.store.patch({ uiHidden: false }); return handled(); }
      return;
    }
    if (k === 'g') { app.store.patch({ uiHidden: !app.state.uiHidden }); return handled(); }
    if (k === 'f') { void toggleFullscreen(app); return handled(); }
    if (k === 't') { if (app.hasTiles()) app.store.patch({ tiles: { ...app.state.tiles, show: !app.state.tiles.show } }); return handled(); }
    if (k === '0') { app.fitView(); return handled(); }
    if (k === '+' || k === '=') { app.zoomBy(1.5); return handled(); }
    if (k === '-' || k === '_') { app.zoomBy(1 / 1.5); return handled(); }
    if (k === '[' || ev.key === 'ArrowLeft') { app.stepExport(-1); return handled(); }
    if (k === ']' || ev.key === 'ArrowRight') { app.stepExport(+1); return handled(); }
    if (k === ' ') { app.togglePlay(); return handled(); }
    if (k === 'r') { app.replay(); return handled(); }
    if (k === 's') { void cap.captureWholeView(); return handled(); }
    if (k === 'd') { cap.setPicking(!cap.picking); return handled(); }
    if (/^[1-9]$/.test(k)) {
      const curves = app.orderedCurves();
      const c = curves[Number(k) - 1];
      if (c) { app.switchCurve(c.id); return handled(); }
    }
  });
}
